import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactFlow, {
  ReactFlowProvider, Background, Controls, MiniMap,
  useNodesState, useEdgesState, MarkerType, useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import api from "../../lib/api";
import StageNode from "./StageNode";
import ViewNode from "./ViewNode";
import { computeLayout } from "./layout";
import { Filter, Network, RotateCcw, Layers as LayersIcon } from "lucide-react";

// Declared OUTSIDE the component to prevent the React Flow
// "It looks like you've created a new nodeTypes/edgeTypes object" warning
// and to avoid re-mounting nodes on every render.
const nodeTypes = Object.freeze({ stage: StageNode, view: ViewNode });
const edgeTypes = Object.freeze({});

const STATUS_COLORS = {
  not_started: "#475569",
  in_progress: "#eab308",
  complete: "#22c55e",
  approved: "#22c55e",
  revision_required: "#f97316",
  blocked: "#ef4444",
};

function OverviewFishboneInner({ projectId, onOpenStage, onOpenView }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");          // all | critical | discipline | status
  const [discipline, setDiscipline] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showViews, setShowViews] = useState(true);
  const [hovered, setHovered] = useState(null);         // node id under cursor
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [resetTick, setResetTick] = useState(0);
  const rfInstance = useReactFlow();
  const rfRef = React.useRef(rfInstance);
  rfRef.current = rfInstance;

  const persistKey = `rachana.fishbone.positions.${projectId}`;

  // ── Fetch overview payload ─────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    setErr("");
    api.get(`/projects/${projectId}/overview`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setErr(e?.response?.data?.detail || e.message));
    return () => { alive = false; };
  }, [projectId]);

  // ── Build the React Flow graph from the payload ───────────────────
  useEffect(() => {
    if (!data) return;

    const persisted = (() => {
      try { return JSON.parse(localStorage.getItem(persistKey) || "{}"); }
      catch { return {}; }
    })();

    const layoutPos = computeLayout({
      stages: data.stages,
      views: data.views,
      edges: data.dependencies,
      includeViews: showViews,
    });

    // Determine which nodes are visible per the active filter.
    const stageVisible = (s) => {
      if (filter === "critical") return s.is_critical_path;
      if (filter === "discipline") return discipline === "ALL" || s.discipline_code === discipline;
      if (filter === "status") return statusFilter === "ALL" || s.status === statusFilter;
      return true;
    };

    const newNodes = [];
    const visibleStageIds = new Set();
    for (const s of data.stages) {
      if (!stageVisible(s)) continue;
      visibleStageIds.add(s.id);
      const pos = persisted[s.id] || layoutPos[s.id] || { x: 0, y: 0 };
      newNodes.push({
        id: s.id,
        type: "stage",
        position: pos,
        data: { ...s, code: s.id, highlight: false, dim: false },
      });
    }
    if (showViews) {
      for (const v of data.views) {
        const pos = persisted[v.node_id] || layoutPos[v.node_id] || { x: 0, y: 0 };
        newNodes.push({
          id: v.node_id,
          type: "view",
          position: pos,
          data: { ...v, highlight: false, dim: false },
        });
      }
    }

    const newEdges = [];
    for (const e of data.dependencies) {
      if (!visibleStageIds.has(e.source) && !e.source.startsWith("view_")) continue;
      const targetIsView = e.target.startsWith("view_");
      if (targetIsView && !showViews) continue;
      if (!targetIsView && !visibleStageIds.has(e.target)) continue;

      const isCp = !!e.is_critical_path;
      const isFb = e.type === "feedback";
      const isSoft = e.type === "soft";
      const isFeeds = e.type === "feeds";
      const stroke = isFb ? "#ef4444" : isCp ? "#3b82f6" : isFeeds ? "#475569" : isSoft ? "#64748b" : "#94a3b8";
      newEdges.push({
        id: e.id,
        source: e.source,
        target: e.target,
        type: isFb ? "smoothstep" : isFeeds ? "straight" : "smoothstep",
        animated: isFb,
        // Persist semantic flags so the hover effect can recolour without
        // rebuilding the entire array.
        data: { isCp, isFb, isSoft, isFeeds, stroke },
        style: {
          stroke,
          strokeWidth: isCp ? 2.5 : isFb ? 1.5 : isFeeds ? 0.8 : 1,
          strokeDasharray: isSoft ? "4,3" : isFeeds ? "2,3" : undefined,
          opacity: isFeeds ? 0.18 : 0.85,
          transition: "opacity 0.18s ease",
        },
        markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 14, height: 14 },
      });
    }

    setNodes(newNodes);
    setEdges(newEdges);
  }, [data, filter, discipline, statusFilter, showViews, persistKey, setNodes, setEdges, resetTick]);

  // ── Hover-only effect (cheap) ─────────────────────────────────────
  // Updates dim/highlight on existing nodes/edges WITHOUT rebuilding the
  // whole graph. Rebuilding while the cursor is over a node caused the
  // node to remount, the cursor to "leave", setHovered(null), rebuild
  // again, cursor "enters", setHovered(id), … infinite flicker. Now we
  // mutate `data` in-place via setNodes/setEdges callback form so node
  // identity is preserved and React Flow does not remount.
  useEffect(() => {
    if (!data) return;
    const deps = data.dependencies;
    const adj = (a) => {
      if (!hovered || a === hovered) return true;
      for (const e of deps) {
        if (e.source === hovered && e.target === a) return true;
        if (e.target === hovered && e.source === a) return true;
      }
      return false;
    };
    setNodes((curr) => curr.map((n) => {
      const wantHighlight = hovered === n.id;
      const wantDim = !!hovered && hovered !== n.id && !adj(n.id);
      if (n.data.highlight === wantHighlight && n.data.dim === wantDim) return n;
      return { ...n, data: { ...n.data, highlight: wantHighlight, dim: wantDim } };
    }));
    setEdges((curr) => curr.map((e) => {
      const inHover = !!hovered && (e.source === hovered || e.target === hovered);
      const wantOpacity = !hovered
        ? (e.data?.isFeeds ? 0.18 : 0.85)
        : inHover ? 0.95 : 0.06;
      if ((e.style?.opacity ?? null) === wantOpacity) return e;
      return { ...e, style: { ...(e.style || {}), opacity: wantOpacity } };
    }));
  }, [hovered, data, setNodes, setEdges]);

  // Fit the viewport to the freshly-laid-out nodes whenever the data, filter,
  // or views toggle changes. React Flow's built-in `fitView` prop only runs
  // once on mount — before the async data arrives — so we re-fit manually.
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => {
      try { rfRef.current?.fitView({ padding: 0.15, duration: 200 }); } catch {}
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filter, discipline, statusFilter, showViews, resetTick]);

  // ── Persist drag-to-reposition ────────────────────────────────────
  const onNodeDragStop = useCallback((_evt, node) => {
    let map = {};
    try { map = JSON.parse(localStorage.getItem(persistKey) || "{}"); } catch {}
    map[node.id] = { x: node.position.x, y: node.position.y };
    localStorage.setItem(persistKey, JSON.stringify(map));
  }, [persistKey]);

  const resetLayout = () => {
    try { localStorage.removeItem(persistKey); } catch {}
    setResetTick((t) => t + 1);
  };

  const onNodeClick = useCallback((_evt, node) => {
    // Belt-and-suspenders: trust either node.type OR the node id prefix
    // (some React Flow versions clobber `type` after re-renders).
    const isView = node.type === "view" || (typeof node.id === "string" && node.id.startsWith("view_"));
    if (isView) {
      onOpenView?.(node.id.replace(/^view_/, ""));
    } else {
      onOpenStage?.(node.id);
    }
  }, [onOpenStage, onOpenView]);

  if (err) {
    return (
      <div className="pf-sev pf-sev-error" style={{ padding: 16, margin: 12 }} data-testid="fishbone-err">
        Could not load overview: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 24, color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace" }} data-testid="fishbone-loading">
        Loading project overview…
      </div>
    );
  }

  return (
    <div data-testid="overview-fishbone" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Summary strip */}
      <SummaryStrip summary={data.summary} />

      {/* Toolbar */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          padding: "10px 14px", background: "var(--pf-surface-2)",
          border: "1px solid var(--pf-border)", borderRadius: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--pf-text-muted)" }}>
          <Filter size={14} />
          <span className="pf-overline" style={{ marginRight: 4 }}>Show</span>
          <select
            data-testid="fishbone-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pf-mono"
            style={{
              background: "var(--pf-surface)", color: "var(--pf-text)",
              border: "1px solid var(--pf-border)", padding: "4px 8px",
              fontSize: 12, borderRadius: 4,
            }}
          >
            <option value="all">All stages</option>
            <option value="critical">Critical path only</option>
            <option value="discipline">By discipline</option>
            <option value="status">By status</option>
          </select>
          {filter === "discipline" && (
            <select
              data-testid="fishbone-discipline"
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value)}
              className="pf-mono"
              style={{
                background: "var(--pf-surface)", color: "var(--pf-text)",
                border: "1px solid var(--pf-border)", padding: "4px 8px",
                fontSize: 12, borderRadius: 4,
              }}
            >
              <option value="ALL">All disciplines</option>
              {["A","B","C","D","E","F","G","H","I","J"].map((d) => (
                <option key={d} value={d}>{d} — {disciplineLabel(d)}</option>
              ))}
            </select>
          )}
          {filter === "status" && (
            <select
              data-testid="fishbone-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pf-mono"
              style={{
                background: "var(--pf-surface)", color: "var(--pf-text)",
                border: "1px solid var(--pf-border)", padding: "4px 8px",
                fontSize: 12, borderRadius: 4,
              }}
            >
              <option value="ALL">All statuses</option>
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="complete">Complete</option>
              <option value="approved">Approved</option>
              <option value="revision_required">Revision required</option>
              <option value="blocked">Blocked</option>
            </select>
          )}
        </div>

        <label
          data-testid="fishbone-toggle-views"
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--pf-text-muted)", cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={showViews}
            onChange={(e) => setShowViews(e.target.checked)}
          />
          <Network size={13} /> Show Views connections
        </label>

        <div style={{ flex: 1 }} />
        <button
          type="button"
          data-testid="fishbone-reset"
          onClick={resetLayout}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "transparent", border: "1px solid var(--pf-border)",
            color: "var(--pf-text-muted)", padding: "5px 10px", borderRadius: 4,
            fontSize: 11.5, cursor: "pointer",
          }}
        >
          <RotateCcw size={13} /> Reset layout
        </button>
      </div>

      <Legend />

      {/* React Flow canvas */}
      <div
        style={{
          height: 720,
          background: "#0a0e14",
          border: "1px solid var(--pf-border)",
          borderRadius: 6,
          overflow: "hidden",
        }}
        data-testid="fishbone-canvas"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          onNodeClick={onNodeClick}
          onNodeMouseEnter={(_e, n) => setHovered(n.id)}
          onNodeMouseLeave={() => setHovered(null)}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          minZoom={0.2}
          maxZoom={2}
          panOnDrag
          zoomOnScroll
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#1f2937" gap={24} size={1} />
          <Controls position="bottom-right" showInteractive={false} />
          <MiniMap
            position="bottom-left"
            pannable
            zoomable
            nodeColor={(n) => {
              if (n.type === "view") return "#1e88e5";
              return STATUS_COLORS[n.data?.status] || "#475569";
            }}
            style={{ background: "#0f172a", border: "1px solid #1f2937" }}
          />
        </ReactFlow>
      </div>

      {hovered && <HoverDetails id={hovered} data={data} />}
    </div>
  );
}

function isAdjacent(target, hovered, deps) {
  if (target === hovered) return true;
  for (const e of deps) {
    if (e.source === hovered && e.target === target) return true;
    if (e.target === hovered && e.source === target) return true;
  }
  return false;
}

function disciplineLabel(d) {
  return ({
    A: "Design Basis", B: "Process", C: "Mechanical", D: "Piping",
    E: "Electrical", F: "Instruments", G: "Civil/Struct", H: "Fire", I: "Procurement", J: "IFC",
  })[d] || d;
}

function SummaryStrip({ summary }) {
  if (!summary) return null;
  const Cell = ({ label, value, color }) => (
    <div style={{ flex: 1, padding: "8px 12px" }}>
      <div className="pf-overline" style={{ marginBottom: 2, color: "var(--pf-text-muted)" }}>{label}</div>
      <div style={{ fontSize: 18, color: color || "var(--pf-text)", fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    </div>
  );
  return (
    <div
      style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 6,
        gap: 1, overflow: "hidden",
      }}
      data-testid="fishbone-summary"
    >
      <Cell label="Total stages"      value={summary.total_stages} />
      <Cell label="Complete"          value={summary.complete} color="#22c55e" />
      <Cell label="In progress"       value={summary.in_progress} color="#eab308" />
      <Cell label="Not started"       value={summary.not_started} color="#94a3b8" />
      <Cell label="Overall progress"  value={`${summary.overall_progress_pct}%`} />
      <Cell label="Critical path"     value={`${summary.critical_path_progress_pct}%`} color="#3b82f6" />
    </div>
  );
}

function Legend() {
  const Item = ({ swatch, label, dashed, animated }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: 22, height: 0,
          borderTop: `${animated ? 2 : 2}px ${dashed ? "dashed" : "solid"} ${swatch}`,
          display: "inline-block",
        }}
      />
      <span style={{ fontSize: 11, color: "var(--pf-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{label}</span>
    </div>
  );
  return (
    <div
      style={{
        display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center",
        padding: "6px 14px", color: "var(--pf-text-muted)", fontSize: 11,
      }}
      data-testid="fishbone-legend"
    >
      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><LayersIcon size={12} /> Legend:</span>
      <Item swatch="#3b82f6" label="Critical path" />
      <Item swatch="#94a3b8" label="Hard dep" />
      <Item swatch="#64748b" label="Soft dep" dashed />
      <Item swatch="#475569" label="Feeds view" dashed />
      <Item swatch="#ef4444" label="Feedback loop" animated />
    </div>
  );
}

function HoverDetails({ id, data }) {
  const isView = id.startsWith("view_");
  if (isView) {
    const v = data.views.find((x) => x.node_id === id);
    if (!v) return null;
    const fedBy = data.dependencies.filter((e) => e.target === id).map((e) => e.source);
    return (
      <div style={detailStyle} data-testid="fishbone-hover">
        <div className="pf-overline" style={{ color: "var(--pf-spark)" }}>VIEW · {v.name}</div>
        <div style={{ fontSize: 12, color: "var(--pf-text-muted)", margin: "4px 0" }}>{v.blurb}</div>
        <div style={{ fontSize: 12 }}><b>Items:</b> {v.count_label}</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          <b>Fed by ({fedBy.length}):</b>{" "}
          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--pf-text-muted)" }}>
            {fedBy.slice(0, 18).join(", ")}{fedBy.length > 18 ? `, +${fedBy.length - 18} more` : ""}
          </span>
        </div>
      </div>
    );
  }
  const s = data.stages.find((x) => x.id === id);
  if (!s) return null;
  const preds = data.dependencies.filter((e) => e.target === id && e.type !== "feeds" && e.type !== "feedback").map((e) => e.source);
  const succs = data.dependencies.filter((e) => e.source === id && !e.target.startsWith("view_") && e.type !== "feedback").map((e) => e.target);
  const feeds = data.dependencies.filter((e) => e.source === id && e.target.startsWith("view_")).map((e) => e.target.replace(/^view_/, ""));
  return (
    <div style={detailStyle} data-testid="fishbone-hover">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--pf-spark)", fontSize: 12 }}>{s.id}</span>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</span>
        <span
          style={{
            fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
            padding: "2px 8px", borderRadius: 3,
            background: STATUS_COLORS[s.status] || "#475569", color: "#fff",
          }}
        >
          {(s.status || "not_started").replace("_", " ").toUpperCase()}
        </span>
        {s.is_critical_path && <span style={{ fontSize: 10, color: "#3b82f6" }}>★ CRITICAL PATH</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}><b>Discipline:</b> {s.discipline}</div>
      <div style={{ fontSize: 12 }}><b>Stage predecessors:</b> {preds.length ? preds.join(", ") : "—"}</div>
      <div style={{ fontSize: 12 }}><b>Stage successors:</b> {succs.length ? succs.slice(0, 24).join(", ") : "—"}{succs.length > 24 ? `, +${succs.length - 24}` : ""}</div>
      <div style={{ fontSize: 12 }}><b>Feeds views:</b> {feeds.length ? feeds.join(", ") : "—"}</div>
    </div>
  );
}

const detailStyle = {
  position: "sticky",
  bottom: 0,
  margin: "0 6px",
  padding: "10px 14px",
  background: "var(--pf-surface-2)",
  border: "1px solid var(--pf-border)",
  borderRadius: 6,
  fontSize: 12,
  color: "var(--pf-text)",
  zIndex: 5,
};

export default function OverviewFishbone(props) {
  return (
    <ReactFlowProvider>
      <OverviewFishboneInner {...props} />
    </ReactFlowProvider>
  );
}
